import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save } from "lucide-react";
import type { ClaimStatusTemplate } from "@/api/generated/client/models/ClaimStatusTemplate";

interface EditStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status: ClaimStatusTemplate | null;
  onStatusChange: (status: ClaimStatusTemplate) => void;
  onSave: (status?: ClaimStatusTemplate) => void;
  isLoading?: boolean;
}

export function EditStatusDialog({
  open,
  onOpenChange,
  status,
  onStatusChange,
  onSave,
  isLoading
}: EditStatusDialogProps) {
  // Local state to handle inputs before syncing up to parent on change 
  // or just use direct controlled props if parent updates are cheap.
  // Using direct controlled props for simplicity as per original design.

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Status: {status?.displayName}</DialogTitle>
          <DialogDescription>
            Update the details of this workflow status.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="status-name">Status Name</Label>
            <Input
              id="status-name"
              value={status?.displayName || ""}
              onChange={(e) =>
                status && onStatusChange({ ...status, displayName: e.target.value })
              }
              placeholder="Enter status name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="status-description">Description (Optional)</Label>
            <Input
              id="status-description"
              value={status?.description || ""}
              onChange={(e) =>
                status && onStatusChange({ ...status, description: e.target.value })
              }
              placeholder="Enter status description"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="status-color">Color (Hex Code)</Label>
            <div className="flex gap-2">
              <Input
                id="status-color"
                value={status?.color || ""}
                onChange={(e) =>
                  status && onStatusChange({ ...status, color: e.target.value })
                }
                placeholder="#000000"
              />
              <div
                className="w-10 h-10 rounded border shrink-0"
                style={{ backgroundColor: status?.color || '#ffffff' }}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => status && onSave(status)} disabled={isLoading}>
            {isLoading && <span className="mr-2 animate-spin">⏳</span>}
            <Save className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
